import * as React from 'react';

import { Controller, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { Box } from '@mui/system';
import { Button } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import {
    CategorieDArticleJsonldCategorieArticleAll,
    CategorieDArticleApiApiCategoriesArticlesPostRequest,
    CategorieDArticleApiApiCategoriesArticlesIdPutRequest,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { domainSelector, useOffreService, useSociete } from '@europrocurement/l2d-domain';
import { useApiRequest } from '@europrocurement/l2d-hooks';

import TextField from '../components/form/TextField';
import SelectField from '../components/FormInputs/SelectField';
import { formatOptionsByKeys } from '../forms/options';
import { AbstractFormType } from '../forms/types';
import { articleCategorySchema, ArticleCategorySchema } from './schema';
import { input, output } from './transformApiEntity';

type ArticleCategoryFormProps = AbstractFormType & {
    entity?: CategorieDArticleJsonldCategorieArticleAll;
    onCancel?: () => void;
};

/** Catégorie d'article Form (Libellé + Domaine). */
const Form: React.FunctionComponent<ArticleCategoryFormProps> = function (props) {
    const { afterSubmit, entity, isCreate = false, onCancel, onLabelChange } = props;

    const { articleCategoryModel: model } = useOffreService();
    const companyId = useSociete();
    const { request } = useApiRequest();

    const defaultValues = input(isCreate ? undefined : entity) as FieldValues;
    const formContext = useForm({
        mode: 'onBlur',
        defaultValues,
        resolver: zodResolver(articleCategorySchema),
    });

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        watch,
    } = formContext;

    // Remonte le libellé courant vers la fiche pour l'afficher dans l'en-tête.
    const labelValue = watch('libelle');
    React.useEffect(() => {
        onLabelChange?.(labelValue ?? '');
    }, [labelValue, onLabelChange]);

    const fetchedDomainsOptions = useSelector(domainSelector).main.data;
    const domainsOptions = formatOptionsByKeys(fetchedDomainsOptions, 'id', 'libelle');

    const successCallback = () => {
        afterSubmit();
    };

    const createModel: SubmitHandler<ArticleCategorySchema> = async (formValues) => {
        const payload = output(formValues, companyId);
        const createRequest = model.create({
            categorieDArticleJsonldCategorieArticleCreate: payload,
        } as CategorieDArticleApiApiCategoriesArticlesPostRequest);

        try {
            await request(createRequest, {
                withToaster: false,
                successCallback: (result) => {
                    enqueueSnackbar("La catégorie d'article a bien été créée");
                    afterSubmit(result.data?.id);
                },
            });
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la création de la catégorie', {
                variant: 'error',
            });
        }
    };

    const updateModel: SubmitHandler<ArticleCategorySchema> = async (formValues) => {
        const id = entity?.id ?? null;
        if (!id) {
            return;
        }
        const payload = output(formValues, companyId);

        try {
            await request(
                model.update({
                    id: id.toString(),
                    categorieDArticleJsonldCategorieArticleCreate: payload,
                } as CategorieDArticleApiApiCategoriesArticlesIdPutRequest),
                {
                    withToaster: false,
                    successCallback: () => {
                        enqueueSnackbar("La catégorie d'article a bien été mise à jour");
                        successCallback();
                    },
                },
            );
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la mise à jour de la catégorie', {
                variant: 'error',
            });
        }
    };

    const onSubmit = (formData: ArticleCategorySchema) =>
        isCreate ? createModel(formData) : updateModel(formData);

    const onError = (formData: unknown) => console.error('ON ERROR', formData);

    return (
        <form
            onSubmit={handleSubmit(onSubmit, onError)}
            noValidate
        >
            <Box sx={{ minWidth: '600px' }}>
                <Controller
                    name="libelle"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            sx={{ width: '100%' }}
                            {...field}
                            name={field.name}
                            label="Libellé"
                            errors={errors}
                            required
                        />
                    )}
                />
                <Controller
                    name="domainId"
                    control={control}
                    render={({ field }) => (
                        <SelectField
                            sx={{ width: '100%' }}
                            {...field}
                            name={field.name}
                            label="Domaine"
                            placeholder="Sélectionnez un domaine"
                            options={domainsOptions}
                            errors={errors}
                            required
                        />
                    )}
                />

                <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                >
                    <Button
                        type="button"
                        sx={{ marginRight: '10px' }}
                        variant="outlined"
                        style={{ marginTop: '2rem', marginBottom: '16px' }}
                        onClick={onCancel}
                    >
                        Annuler
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        variant="contained"
                        style={{ marginTop: '2rem', marginBottom: '16px' }}
                    >
                        Valider
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default Form;
